import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { isPathActive } from "../../config/app-menu.jsx";

/**
 * Minified-mode float submenu — React portal (replaces DOM cloning).
 */
function SidebarFloatSubmenu({
  item,
  pathname,
  anchorRect,
  sidebarLeft,
  dark,
  accent,
  onKeepOpen,
  onRequestClose,
  onDismiss,
  onNavigate,
}) {
  const panelRef = useRef(null);
  const [style, setStyle] = useState({
    top: anchorRect?.top ?? 0,
    left: sidebarLeft,
    bottom: "auto",
    arrowTop: 20,
    arrowBottom: "auto",
    overflow: false,
  });

  useLayoutEffect(() => {
    if (!anchorRect || !panelRef.current) return;

    const windowHeight = document.body.clientHeight;
    const targetTop = anchorRect.top;
    const targetHeight = panelRef.current.clientHeight;
    const overflow = targetHeight > windowHeight;

    if (windowHeight - targetTop > targetHeight) {
      setStyle({
        top: targetTop,
        left: sidebarLeft,
        bottom: "auto",
        arrowTop: 20,
        arrowBottom: "auto",
        overflow,
      });
    } else {
      const arrowBottom = windowHeight - targetTop - 21;
      setStyle({
        top: "auto",
        left: sidebarLeft,
        bottom: 0,
        arrowTop: "auto",
        arrowBottom,
        overflow,
      });
    }
  }, [anchorRect, sidebarLeft, item]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  if (!item?.children?.length || !anchorRect) return null;

  const panel = (
    <div
      id="app-sidebar-float-submenu"
      ref={panelRef}
      className="app-sidebar-float-submenu-container"
      data-bs-theme={dark ? "dark" : undefined}
      data-offset-top={anchorRect.top}
      data-menu-offset-top={anchorRect.top}
      style={{
        top: style.top === "auto" ? "auto" : `${style.top}px`,
        left: `${style.left}px`,
        bottom: style.bottom === 0 ? 0 : "auto",
        right: "auto",
        "--sidebar-accent": accent || "#30D6A4",
        "--sidebar-accent-soft": `${accent || "#30D6A4"}24`,
      }}
      onMouseEnter={onKeepOpen}
      onMouseLeave={onRequestClose}
    >
      <div
        className="app-sidebar-float-submenu-arrow"
        id="app-sidebar-float-submenu-arrow"
        style={{
          top: style.arrowTop === "auto" ? "auto" : `${style.arrowTop}px`,
          bottom:
            style.arrowBottom === "auto" ? "auto" : `${style.arrowBottom}px`,
        }}
      />
      <div
        className="app-sidebar-float-submenu-line"
        id="app-sidebar-float-submenu-line"
        style={{
          top: style.arrowTop === "auto" ? "20px" : `${style.arrowTop}px`,
          bottom:
            style.arrowBottom === "auto" ? "auto" : `${style.arrowBottom}px`,
        }}
      />
      <div
        className={`app-sidebar-float-submenu${
          style.overflow ? " overflow-scroll mh-100vh" : ""
        }`}
      >
        {item.children.map((child) => {
          const active = isPathActive(pathname, child.match || child.path, {
            end: Boolean(child.end),
          });
          return (
            <div
              key={child.path}
              className={`menu-item${active ? " active" : ""}`}
            >
              <NavLink
                to={child.path}
                className="menu-link"
                end={Boolean(child.end)}
                onClick={() => {
                  onNavigate?.();
                  onDismiss();
                }}
              >
                <div className="menu-text">{child.title}</div>
              </NavLink>
            </div>
          );
        })}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}

export default SidebarFloatSubmenu;
